import { get as idbGet, set as idbSet, del as idbDel, keys as idbKeys } from "idb-keyval";
import { BACKEND_URL } from "@/constants/api";

/**
 * Two-tier cache for folder logos (and, later, signatures).
 *
 * The cheap `logosVersion` token rides the normal folder payload. We only fetch
 * the heavy base64 bytes when the cached version is stale or missing:
 *   L1: in-memory Map (survives SPA navigation, lost on hard refresh)
 *   L2: IndexedDB    (survives reload / new session / bulk export)
 *
 * Keys are scoped by userId so one account can't read another's cached images,
 * and the whole cache is cleared on sign-out (shared-computer safety).
 */

const memory = new Map(); // key -> { version, logos }

const keyFor = (userId, folderId) => `logos:${userId}:${folderId}`;

/**
 * Return the logos for a folder, using the cache when the version matches.
 *
 * @param {object}   opts
 * @param {string}   opts.userId        - current user's id (cache scope)
 * @param {string}   opts.folderId      - report folder id
 * @param {string?}  opts.version       - folder.logosVersion from the cheap payload
 * @param {function} opts.getToken      - Clerk getToken
 * @returns {Promise<Array>} the logos array (possibly empty)
 */
export async function getFolderLogos({ userId, folderId, version, getToken }) {
  if (!userId || !folderId) return [];

  // No version means the folder has no logos — nothing to fetch, clear any stale entry.
  if (!version) {
    memory.delete(keyFor(userId, folderId));
    return [];
  }

  const key = keyFor(userId, folderId);

  // L1
  const mem = memory.get(key);
  if (mem && mem.version === version) return mem.logos;

  // L2
  try {
    const stored = await idbGet(key);
    if (stored && stored.version === version) {
      memory.set(key, stored);
      return stored.logos;
    }
  } catch { /* IndexedDB unavailable (private mode); fall through to network */ }

  // Miss (no cached copy, or cached version is stale) — fetch the bytes.
  // We deliberately DON'T send If-None-Match here: staleness is already decided
  // at the app layer by comparing folder.logosVersion to the cached version, so
  // by the time we reach a fetch we always want the full payload. Sending the
  // folder's version would match the server ETag and 304 us into an empty result.
  const token = await getToken();
  const res = await fetch(`${BACKEND_URL}api/report-folders/${folderId}/logos`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!res.ok) return mem?.logos ?? [];

  const json = await res.json();
  const entry = { version: json.logosVersion ?? version, logos: json.logos ?? [] };
  memory.set(key, entry);
  try { await idbSet(key, entry); } catch { /* best-effort persistence */ }
  return entry.logos;
}

/** Drop a single folder's cached logos from both tiers (call after a save). */
export async function invalidateFolderLogos(userId, folderId) {
  const key = keyFor(userId, folderId);
  memory.delete(key);
  try { await idbDel(key); } catch { /* ignore */ }
}

/** Clear every cached image. Call on sign-out so nothing lingers on shared devices. */
export async function clearImageCache() {
  memory.clear();
  try {
    const all = await idbKeys();
    await Promise.all(
      all
        .filter((k) => typeof k === "string" && (k.startsWith("logos:") || k.startsWith("sig:")))
        .map((k) => idbDel(k))
    );
  } catch { /* ignore */ }
}
